import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Mail, Send, FileText, Plus, ArrowRight, Loader2, Database, CheckCircle2, Clock, LayoutDashboard, Shield, ExternalLink } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { campaignsApi, signupFormsApi, teamsApi, usersApi, CampaignRecord, SignupFormRecord, TeamRecord, UserRecord } from '../services/creatorApi';
import { crmContactsApi, crmMailingListsApi, CrmContact, CrmMailingList } from '../services/crmApi';
import { toast } from 'sonner';
export function Dashboard() {
  const {
    currentWorkspace,
    currentUser,
    currentSubRegion
  } = useWorkspace();
  const [loading, setLoading] = useState(true);
  // Data state
  const [stats, setStats] = useState({
    contacts: 0,
    lists: 0,
    campaigns: 0,
    forms: 0
  });
  const [recentCampaigns, setRecentCampaigns] = useState<CampaignRecord[]>([]);
  const [recentLists, setRecentLists] = useState<CrmMailingList[]>([]);
  // Super Admin Data
  const [allTeams, setAllTeams] = useState<TeamRecord[]>([]);
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch data in parallel
        const [campaignsData, formsData, listsData, contactsData,
        // Note: fetching all contacts might be heavy, consider optimization later
        teamsData, usersData] = await Promise.all([campaignsApi.list(), signupFormsApi.list(), crmMailingListsApi.list(), crmContactsApi.list(1, 1), currentUser.Role === 'Super Admin' ? teamsApi.list() : Promise.resolve([]), currentUser.Role === 'Super Admin' ? usersApi.list() : Promise.resolve([])]);
        // Filter by current workspace/team
        const teamCampaigns = campaignsData.filter(c => c.Team === currentWorkspace.name && (!currentSubRegion || !c.Region || c.Region === currentSubRegion));
        const teamForms = formsData.filter(f => f.Team === currentWorkspace.name && (!currentSubRegion || !f.Region || f.Region === currentSubRegion));
        const teamLists = listsData.filter(l => l.Team === currentWorkspace.name && (!currentSubRegion || !l.Region || l.Region === currentSubRegion));
        // Sort campaigns by date (newest first)
        // Note: CampaignRecord doesn't always have Created_Time, using ID or sorting by list order if assumed chronological
        // Or check if there's a date field. CampaignRecord has Exported_At.
        const sortedCampaigns = [...teamCampaigns].sort((a, b) => {
          // Fallback to ID comparison if no date, assuming newer IDs are higher/lexicographically later
          return (b.Exported_At || b.ID).localeCompare(a.Exported_At || a.ID);
        }).slice(0, 5);
        setRecentCampaigns(sortedCampaigns);
        setRecentLists(teamLists.slice(0, 5));
        // For contacts count, since we only fetched one page, we might not have the total.
        // crmContactsApi.list doesn't return count.
        // We'll use a placeholder or the length of lists subscriber counts if available.
        // Actually, let's just sum up subscriber counts from mailing lists as a proxy,
        // or fetch all contacts if the user base is small.
        // For now, let's try to fetch all contacts to get an accurate count,
        // but handle errors gracefully if it's too large.
        let contactCount = 0;
        try {
          // Attempt to fetch all for accurate count (as per requirement "count from crmContactsApi.list()")
          // If list() returns paginated, we might need listAll().
          // Let's use listAll() but be careful.
          const allContacts = await crmContactsApi.listAll();
          // Filter by team
          const teamContacts = allContacts.filter(c => c.Team === currentWorkspace.name && (!currentSubRegion || !c.Sub_Region || c.Sub_Region === currentSubRegion));
          contactCount = teamContacts.length;
        } catch (e) {
          console.warn('Failed to fetch all contacts count', e);
          // Fallback: sum of mailing list subscribers
          contactCount = teamLists.reduce((acc, list) => acc + (list.Subscriber_Count || 0), 0);
        }
        setStats({
          contacts: contactCount,
          lists: teamLists.length,
          campaigns: teamCampaigns.length,
          forms: teamForms.length
        });
        if (currentUser.Role === 'Super Admin') {
          setAllTeams(teamsData);
          setAllUsers(usersData);
        }
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, [currentWorkspace.name, currentSubRegion, currentUser.Role]);
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Sent':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-2284">
            <CheckCircle2 className="h-3 w-3" data-id="element-2285" />
            Sent
          </span>;
      case 'Exported':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-2286">
            <Database className="h-3 w-3" data-id="element-2287" />
            Exported
          </span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-2288">
            <FileText className="h-3 w-3" data-id="element-2289" />
            Draft
          </span>;
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]" data-id="element-2290">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-2291" />
      </div>;
  }
  return <div className="space-y-8 pb-12 animate-in fade-in duration-500" data-id="element-2292">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2293">
        <div data-id="element-2294">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-2295">
            Dashboard
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-2296">
            Welcome back, {currentUser.User_Name}. Here's what's happening in{' '}
            {currentWorkspace.name}.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-2297">
          <span className="text-sm text-gray-500 bg-white px-3 py-1.5 rounded-md border border-gray-200 shadow-sm flex items-center gap-2" data-id="element-2298">
            <Clock className="h-4 w-4" data-id="element-2299" />
            {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2300">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2301">
          <div className="flex items-center justify-between" data-id="element-2302">
            <div data-id="element-2303">
              <p className="text-sm font-medium text-gray-500" data-id="element-2304">
                Total Contacts
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2305">
                {stats.contacts.toLocaleString()}
              </p>
            </div>
            <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-2306">
              <Users className="h-5 w-5 text-blue-600" data-id="element-2307" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-2308">
            <Link to="/contacts" className="flex items-center gap-1" data-id="element-2309">
              View all contacts <ArrowRight className="h-4 w-4" data-id="element-2310" />
            </Link>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2311">
          <div className="flex items-center justify-between" data-id="element-2312">
            <div data-id="element-2313">
              <p className="text-sm font-medium text-gray-500" data-id="element-2314">Mailing Lists</p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2315">
                {stats.lists}
              </p>
            </div>
            <div className="h-10 w-10 bg-purple-50 rounded-lg flex items-center justify-center" data-id="element-2316">
              <Mail className="h-5 w-5 text-purple-600" data-id="element-2317" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-purple-600 hover:text-purple-700 font-medium" data-id="element-2318">
            <Link to="/mailing-lists" className="flex items-center gap-1" data-id="element-2319">
              Manage lists <ArrowRight className="h-4 w-4" data-id="element-2320" />
            </Link>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2321">
          <div className="flex items-center justify-between" data-id="element-2322">
            <div data-id="element-2323">
              <p className="text-sm font-medium text-gray-500" data-id="element-2324">Campaigns</p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2325">
                {stats.campaigns}
              </p>
            </div>
            <div className="h-10 w-10 bg-green-50 rounded-lg flex items-center justify-center" data-id="element-2326">
              <Send className="h-5 w-5 text-green-600" data-id="element-2327" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-green-600 hover:text-green-700 font-medium" data-id="element-2328">
            <Link to="/campaigns" className="flex items-center gap-1" data-id="element-2329">
              View campaigns <ArrowRight className="h-4 w-4" data-id="element-2330" />
            </Link>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2331">
          <div className="flex items-center justify-between" data-id="element-2332">
            <div data-id="element-2333">
              <p className="text-sm font-medium text-gray-500" data-id="element-2334">Signup Forms</p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2335">
                {stats.forms}
              </p>
            </div>
            <div className="h-10 w-10 bg-orange-50 rounded-lg flex items-center justify-center" data-id="element-2336">
              <FileText className="h-5 w-5 text-orange-600" data-id="element-2337" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-orange-600 hover:text-orange-700 font-medium" data-id="element-2338">
            <Link to="/forms" className="flex items-center gap-1" data-id="element-2339">
              Edit forms <ArrowRight className="h-4 w-4" data-id="element-2340" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2341">
        {/* Recent Campaigns - 2/3 width */}
        <div className="lg:col-span-2 space-y-6" data-id="element-2342">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-2343">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-2344">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-2345">
                Recent Campaigns
              </h2>
              <Link to="/campaigns" className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-2346">
                View All
              </Link>
            </div>
            <div className="overflow-x-auto" data-id="element-2347">
              {recentCampaigns.length > 0 ? <table className="min-w-full divide-y divide-gray-200" data-id="element-2348">
                  <thead className="bg-gray-50" data-id="element-2349">
                    <tr data-id="element-2350">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2351">
                        Campaign Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2352">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2353">
                        Recipients
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2354">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-2355">
                    {recentCampaigns.map(campaign => <tr key={campaign.ID} className="hover:bg-gray-50 transition-colors" data-id="element-2356">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2357">
                          <div className="text-sm font-medium text-gray-900" data-id="element-2358">
                            {campaign.Campaign_Name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-2359">
                            {campaign.Email_Subject}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2360">
                          {getStatusBadge(campaign.Status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-2361">
                          {campaign.Recipient_Count?.toLocaleString() || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2362">
                          {formatDate(campaign.Exported_At)}
                        </td>
                      </tr>)}
                  </tbody>
                </table> : <div className="p-8 text-center text-gray-500" data-id="element-2363">
                  No campaigns found.{' '}
                  <Link to="/campaigns/new" className="text-blue-600 hover:underline" data-id="element-2364">
                    Create your first campaign
                  </Link>
                </div>}
            </div>
          </div>

          {/* Super Admin Team Overview */}
          {currentUser.Role === 'Super Admin' && <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-2365">
              <div className="p-6 border-b border-gray-200 flex items-center gap-2" data-id="element-2366">
                <Shield className="h-5 w-5 text-purple-600" data-id="element-2367" />
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-2368">
                  Team Overview (Super Admin)
                </h2>
              </div>
              <div className="overflow-x-auto" data-id="element-2369">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-2370">
                  <thead className="bg-gray-50" data-id="element-2371">
                    <tr data-id="element-2372">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2373">
                        Team Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2374">
                        Region
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2375">
                        Users
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2376">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-2377">
                    {allTeams.map(team => {
                  const teamUsers = allUsers.filter(u => u.Team === team.Team_Name);
                  return <tr key={team.ID} className="hover:bg-gray-50 transition-colors" data-id="element-2378">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2379">
                            <div className="text-sm font-medium text-gray-900" data-id="element-2380">
                              {team.Team_Name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2381">
                            {team.Team_Regions?.map(r => r.Region_Name).join(', ') || 'All Regions'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2382">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2383">
                              {teamUsers.length} users
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-2384">
                            <Link to="/users" className="text-blue-600 hover:text-blue-900" data-id="element-2385">
                              Manage
                            </Link>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>

        {/* Sidebar / Quick Actions - 1/3 width */}
        <div className="space-y-6" data-id="element-2386">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-2387">
            <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-2388">
              Quick Actions
            </h3>
            <div className="space-y-3" data-id="element-2389">
              <Link to="/campaigns/new" className="flex items-center justify-between w-full p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors group" data-id="element-2390">
                <span className="flex items-center gap-3 font-medium" data-id="element-2391">
                  <Send className="h-5 w-5" data-id="element-2392" />
                  Create Campaign
                </span>
                <Plus className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-2393" />
              </Link>

              <Link to="/forms/new" className="flex items-center justify-between w-full p-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors group" data-id="element-2394">
                <span className="flex items-center gap-3 font-medium" data-id="element-2395">
                  <FileText className="h-5 w-5" data-id="element-2396" />
                  New Signup Form
                </span>
                <Plus className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-2397" />
              </Link>

              <Link to="/contacts" className="flex items-center justify-between w-full p-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors group" data-id="element-2398">
                <span className="flex items-center gap-3 font-medium" data-id="element-2399">
                  <Users className="h-5 w-5" data-id="element-2400" />
                  View Contacts
                </span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-2401" />
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-2402">
            <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-2403">
              Active Mailing Lists
            </h3>
            <div className="space-y-4" data-id="element-2404">
              {recentLists.length > 0 ? recentLists.map(list => <div key={list.id} className="flex items-center justify-between pb-3 border-b border-gray-100 last:border-0 last:pb-0" data-id="element-2405">
                    <div className="min-w-0" data-id="element-2406">
                      <p className="text-sm font-medium text-gray-900 truncate" data-id="element-2407">
                        {list.Name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2408">
                        {list.Subscriber_Count || 0} subscribers
                      </p>
                    </div>
                    <Link to={`/mailing-lists/${list.id}`} className="text-gray-400 hover:text-blue-600" data-id="element-2409">
                      <ExternalLink className="h-4 w-4" data-id="element-2410" />
                    </Link>
                  </div>) : <p className="text-sm text-gray-500" data-id="element-2411">No mailing lists found.</p>}
              <Link to="/mailing-lists" className="block text-center text-sm text-blue-600 hover:text-blue-700 font-medium pt-2" data-id="element-2412">
                View All Lists
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>;
}